<script lang="ts" setup>
import Menu from 'primevue/menu'
import type { CartItem } from '~/types/commerce'
import { useItemDisplay } from '~/composables/useItemDisplay'
import { useCommerceStore } from '~/stores/pos/commerce'

interface Props {
  item: CartItem
  context?: 'cart' | 'order'
  showActions?: boolean
  showImage?: boolean
  compactView?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  context: 'cart',
  showActions: false,
  showImage: false,
  compactView: false,
})

const emit = defineEmits<{
  increase: [item: CartItem]
  decrease: [item: CartItem]
  edit: [cartUuid: string]
  remove: [cartUuid: string]
}>()

const { t } = useNuxtApp().$i18n
const menu = ref()

const commerceStore = useCommerceStore()
const { combos } = storeToRefs(commerceStore)

const {
  hasModifierSelections,
  getModifierSelections,
  getComboProductModifiers,
} = useItemDisplay()

const menuItems = computed(() => [
  {
    label: t('Add one'),
    icon: 'pi pi-plus',
    command: () => emit('increase', props.item),
  },
  {
    label: t('Remove one'),
    icon: 'pi pi-minus',
    command: () => emit('decrease', props.item),
  },
  {
    separator: true,
  },
  {
    label: t('Edit'),
    icon: 'pi pi-pencil',
    command: () => emit('edit', props.item.cart_uuid),
  },
  {
    label: t('Remove'),
    icon: 'pi pi-trash',
    class: 'text-red-500',
    command: () => emit('remove', props.item.cart_uuid),
  },
])

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

// Get combo data based on context
const comboData = computed(() => {
  if (props.context === 'order') {
    return props.item.combo_data || props.item
  }
  return props.item
})

// Get combo items based on context
const comboItems = computed(() => {
  if (props.context === 'order') {
    return props.item.combo_data?.items || []
  }
  return props.item.items || []
})

// Calculate price per unit
const pricePerUnit = computed(() => {
  if (props.context === 'order') {
    // For orders, use the price field directly
    return (props.item.price || 0) / (props.item.quantity || 1)
  }
  // For cart, use the calculation function
  const total = commerceStore.calculateCartItemTotal(props.item) || 0
  return total / (props.item.quantity || 1)
})

// Get default modifiers for a product when no custom modifiers exist
function getDefaultModifiersForProduct(product: any): any[] {
  if (!props.item.combo_id || !combos.value) return []

  // Find the original combo definition
  const originalCombo = combos.value.find(combo => combo.id === props.item.combo_id)
  if (!originalCombo?.items) return []

  // Find the group and product that matches this cart item
  for (const group of originalCombo.items) {
    const matchingProduct = group.options?.find((opt: any) =>
      opt.product_id === product.product_id && opt.default,
    )

    if (matchingProduct?.product_modifiers) {
      const defaultModifiers: any[] = []
      matchingProduct.product_modifiers.forEach((modifier: any) => {
        if (modifier.default_option?.length > 0) {
          modifier.default_option.forEach((defaultOpt: any) => {
            defaultModifiers.push({
              id: defaultOpt.id,
              name: defaultOpt.name,
              price_modifier: defaultOpt.price_change || 0,
            })
          })
        }
      })
      return defaultModifiers
    }
  }

  return []
}

// Compact display: show combo items as a single line
const compactItemsText = computed(() => {
  const items = comboItems.value.map((product: any) => product.product_name).join(', ')
  return items.length > 0 ? ` - ${items}` : ''
})
</script>

<template>
  <div class="combo-item-renderer">
    <!-- Compact View -->
    <div
      v-if="compactView"
      class="flex items-center gap-2"
    >
      <div class="grow text-sm">
        <span class="font-semibold">
          <template v-if="item.quantity > 1">{{ item.quantity }} x </template>{{ comboData.name }}
        </span>
        <span class="text-gray-600">{{ compactItemsText }}</span>
        <span
          v-if="item.notes"
          class="text-gray-500 italic ml-2"
        >📝 {{ item.notes }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="font-semibold text-sm whitespace-nowrap">
          {{ usePriceFormatter(context === 'order' ? (item.price || 0) : (commerceStore.calculateCartItemTotal(item) || 0)) }}
        </span>
        <Button
          icon="pi pi-ellipsis-v"
          class="p-button-text p-button-rounded !p-1"
          size="small"
          :aria-label="t('Actions')"
          @click="toggleMenu"
        />
        <Menu
          ref="menu"
          :model="menuItems"
          :popup="true"
        />
      </div>
    </div>

    <!-- Normal View -->
    <div
      v-else
      class="flex gap-2"
    >
      <!-- Combo Image -->
      <div
        v-if="showImage && item.images"
        class="flex-shrink-0"
      >
        <img
          class="size-24 rounded-md"
          :src="getFirstImage(item) || '/product_placeholder.webp'"
          :alt="`Image of ${comboData.name}`"
        >
      </div>

      <!-- Combo Content -->
      <div class="grow">
        <!-- Header with name and price -->
        <div class="flex text-lg">
          <div class="grow">
            <span class="font-semibold">
              {{ item.quantity }} x {{ comboData.name }}
            </span>
          </div>
          <div class="flex">
            <span v-if="item.quantity">{{ item.quantity }} x </span>
            <span class="ml-2">
              {{ usePriceFormatter(pricePerUnit) }}
            </span>
          </div>
        </div>

        <!-- Combo Items -->
        <ul
          v-if="comboItems.length > 0"
          class="list-none p-0 pl-6 mt-2"
        >
          <li
            v-for="product in comboItems"
            :key="product.id || product.product_id"
            class="flex justify-between text-sm"
          >
            <div class="text-left">
              <span>{{ product.quantity }} x </span>
              <span>{{ product.product_name }}</span>

              <!-- Price variation -->
              <span
                v-if="product.price_variation && product.price_variation > 0"
                class="text-black ml-2"
              >
                + {{ usePriceFormatter(product.price_variation) }}
              </span>

              <!-- Product Modifiers -->
              <div v-if="getComboProductModifiers(product)">
                <!-- Handle selected_modifiers (object structure) -->
                <div
                  v-if="product.selected_modifiers && Object.keys(product.selected_modifiers).length > 0"
                  class="pl-5 mt-1"
                >
                  <template
                    v-for="(selections, modifierId) in product.selected_modifiers"
                    :key="modifierId"
                  >
                    <div v-if="Array.isArray(selections) && selections.length > 0">
                      <div
                        v-for="selection in selections"
                        :key="selection.id"
                        class="pl-1"
                      >
                        <span>+ {{ selection.name }}</span>
                        <span
                          v-if="selection.price_modifier > 0"
                          class="font-semibold text-black"
                        > + {{ usePriceFormatter(selection.price_modifier) }}</span>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Handle modifiers array structure -->
                <div
                  v-else-if="product.modifiers && product.modifiers.some(m => hasModifierSelections(m))"
                  class="pl-5 mt-1"
                >
                  <template
                    v-for="modifier in product.modifiers"
                    :key="modifier.group_id"
                  >
                    <template v-if="hasModifierSelections(modifier)">
                      <div
                        v-for="selection in getModifierSelections(modifier)"
                        :key="selection.id"
                        class="pl-1"
                      >
                        <span>+ {{ selection.name }}</span>
                        <span
                          v-if="(selection.price_modifier || selection.price_change || 0) > 0"
                          class="font-semibold text-black"
                        >
                          + {{ usePriceFormatter(selection.price_modifier || selection.price_change) }}
                        </span>
                      </div>
                    </template>
                  </template>
                </div>

                <!-- Default modifiers for regular combos -->
                <div
                  v-else-if="context === 'cart' && getDefaultModifiersForProduct(product).length > 0"
                  class="pl-5 mt-1"
                >
                  <div
                    v-for="defaultModifier in getDefaultModifiersForProduct(product)"
                    :key="defaultModifier.id"
                    class="pl-1"
                  >
                    <span>+ {{ defaultModifier.name }}</span>
                    <span
                      v-if="defaultModifier.price_modifier > 0"
                      class="font-semibold text-black"
                    > + {{ usePriceFormatter(defaultModifier.price_modifier) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>

        <!-- Total -->
        <div
          class="mt-2 pt-2 text-right text-lg border-t border-gray-200"
        >
          <span class="font-semibold">
            {{ usePriceFormatter(context === 'order' ? (item.price || 0) : (commerceStore.calculateCartItemTotal(item) || 0)) }}
          </span>
        </div>

        <!-- Action Buttons (Expanded mode shows circular buttons) -->
        <div
          v-if="showActions"
          class="mt-2 flex justify-end gap-1"
        >
          <Button
            class="p-button-rounded p-button-danger p-2"
            icon="pi pi-minus"
            :aria-label="$t('Remove one')"
            @click="emit('decrease', item)"
          />
          <Button
            class="p-button-rounded border-green-500 bg-green-500 p-2 hover:border-green-700"
            icon="pi pi-plus"
            :aria-label="$t('Add one')"
            @click="emit('increase', item)"
          />
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded p-2"
            :aria-label="$t('Edit Combo')"
            @click="emit('edit', item.cart_uuid)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger p-2"
            :aria-label="$t('Remove')"
            @click="emit('remove', item.cart_uuid)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.combo-item-renderer {
  @apply w-full;
}

/* Print styles */
@media print {
  .combo-item-renderer {
    @apply text-xs;
  }

  .combo-item-renderer .text-lg {
    @apply text-xs;
  }

  .combo-item-renderer .text-sm {
    @apply text-xs;
  }

  .combo-item-renderer ul {
    @apply pl-2;
  }
}
</style>
